// src/pages/ApSelection.js
import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
} from "@carbon/react";
import { ChevronSort, ChevronUp, ChevronDown } from "@carbon/icons-react";
import { useNavigate } from "react-router-dom";
import CustomButton from "../components/CustomButton";
import { useAuth } from "../auth/AuthContext";
import api from "../api";

export default function ApSelection() {
  const navigate = useNavigate();
  const { user, role } = useAuth(); // { id, role }

  const [rows, setRows] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const selectedExpert = useMemo(
    () => rows.find((x) => x.id === selectedId) || null,
    [selectedId, rows]
  );

  useEffect(() => {
    if (role !== "expert_physician" || !user?.id) return;

    const fetchDoctors = async () => {
      try {
        const res = await api.get(`/v1/doctor/attending_doctors/${user.id}`);
        const data = res.data;

        // doctor_id_list: [["D010", "梅田"], ["D011", "千里中央"], ...]
        const doctors = (data.doctor_id_list || [])
          .filter((pair) => Array.isArray(pair) && pair.length >= 2)
          .map(([id, name]) => ({
            id,
            text: name || "-", // 念のためnullチェック
          }));

        setRows(doctors);
      } catch (err) {
        console.error("主治医一覧取得失敗:", err);
        setRows([]);
      }
    };

    fetchDoctors();
  }, [role, user]);

  // ソート（asc → desc → none）
  const [sort, setSort] = useState({ key: null, dir: null });
  const rotateSort = (key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return { key: null, dir: null };
    });
  };
  const getAriaSort = (key) =>
    sort.key !== key || !sort.dir
      ? "none"
      : sort.dir === "asc"
      ? "ascending"
      : "descending";

  const sortedRows = useMemo(() => {
    if (!sort.key || !sort.dir) return rows;
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort(
      (a, b) => a[sort.key].localeCompare(b[sort.key], "ja") * dir
    );
  }, [rows, sort]);

  const handleConfirm = () => {
    if (selectedExpert) {
      sessionStorage.setItem("attending_doctor", JSON.stringify(selectedExpert));
      navigate("/patient-selection", {
        state: {
          doctor: {
            id: selectedExpert.id,
            text: selectedExpert.text,
          },
        },
      });
    }
  };

  const hostRef = useRef(null);
  const tbodyRef = useRef(null);

  // ソートアイコン
  const SortIcon = ({ col }) => {
    if (sort.key !== col || !sort.dir) return <ChevronSort size={16} />;
    return sort.dir === "asc" ? (
      <ChevronUp size={16} />
    ) : (
      <ChevronDown size={16} />
    );
  };
  const SortButton = ({ colKey, children }) => (
    <button
      type="button"
      onClick={() => rotateSort(colKey)}
      style={{
        all: "unset",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: "0.25rem",
      }}
      aria-label={`ソート切替: ${children}`}
      aria-pressed={sort.key === colKey && !!sort.dir}
      title={`ソート切替: ${children}`}
    >
      <span>{children}</span>
      <SortIcon col={colKey} />
    </button>
  );

  return (
    <>
      <div style={{ padding: "1rem", maxWidth: 720, margin: "0 auto" }}>
        <h1 style={{ textAlign: "center", marginBottom: "0.5rem" }}>
          主治医一覧
        </h1>
        <div style={{ textAlign: "center", color: "#525252" }}>
          行をクリックして主治医を選択してください
        </div>

        <Stack gap={7} style={{ marginTop: "1rem" }}>
          <div
            className="table-scroll-host"
            ref={hostRef}
            style={{ marginTop: "1rem" }}
          >
            <TableContainer>
              <Table size="md" style={{ tableLayout: "fixed", minWidth: 520 }}>
                <TableHead>
                  <TableRow>
                    <TableHeader>選択中</TableHeader>
                    <TableHeader aria-sort={getAriaSort("id")}>
                      <SortButton colKey="id">主治医ID</SortButton>
                    </TableHeader>
                    <TableHeader>主治医名</TableHeader>
                  </TableRow>
                </TableHead>

                <TableBody ref={tbodyRef}>
                  {sortedRows.map((d) => {
                    const isSelected = d.id === selectedId;
                    return (
                      <TableRow
                        key={d.id}
                        onClick={() => setSelectedId(d.id)}
                        style={{
                          cursor: "pointer",
                          backgroundColor: isSelected ? "#e5f1ff" : undefined,
                        }}
                      >
                        <TableCell>
                          <div
                            aria-hidden
                            style={{
                              width: 14,
                              height: 14,
                              borderRadius: "50%",
                              border: "2px solid #0f62fe",
                              background: isSelected ? "#0f62fe" : "transparent",
                            }}
                          />
                        </TableCell>
                        <TableCell>{d.id}</TableCell>
                        <TableCell>{d.text}</TableCell>
                      </TableRow>
                    );
                  })}
                  {sortedRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3}>主治医が見つかりません。</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {selectedExpert && (
            <div
              style={{
                marginTop: "0.5rem",
                fontSize: "0.95rem",
                color: "#161616",
                background: "#f4f4f4",
                padding: "0.75rem 1rem",
                borderLeft: "4px solid #0f62fe",
              }}
            >
              選択中：{selectedExpert.id}｜{selectedExpert.text}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <CustomButton
              kind="primary"
              onClick={handleConfirm}
              disabled={!selectedExpert}
            >
              次へ
            </CustomButton>
          </div>
        </Stack>
      </div>

      <style>{`
        .table-scroll-host {
          --w-sel: 56px;
          --w-id: 160px;
          --w-name: 280px;
        }

        .table-scroll-host table {
          table-layout: fixed;
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
        }

        .table-scroll-host thead {
          display: table;
          width: 100%;
          table-layout: fixed;
          box-sizing: border-box;
        }
        .table-scroll-host tbody {
          display: block;
          max-height: 420px;
          overflow: auto;
          width: 100%;
          scrollbar-gutter: stable;

          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .table-scroll-host tbody::-webkit-scrollbar {
          width: 0;
          height: 0;
        }

        .table-scroll-host tbody tr {
          display: table;
          width: 100%;
          table-layout: fixed;
        }

        .table-scroll-host th,
        .table-scroll-host td {
          text-align: left;
          box-sizing: border-box;
          padding-left: 16px !important;
          padding-right: 16px !important;
          vertical-align: middle;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .table-scroll-host thead th { height: 48px; }

        .table-scroll-host thead th:nth-child(1),
        .table-scroll-host tbody td:nth-child(1) { width: var(--w-sel); }
        .table-scroll-host thead th:nth-child(2),
        .table-scroll-host tbody td:nth-child(2) { width: var(--w-id); }
        .table-scroll-host thead th:nth-child(3),
        .table-scroll-host tbody td:nth-child(3) { width: var(--w-name); }
      `}</style>
    </>
  );
}
